import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardEvents } from '../contexts/EventContext';
import * as eventsService from '../services/eventsService';
import * as progressService from '../services/progressService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatEventLocationDisplay } from '../utils/eventLocation';

const { FiCalendar, FiPlay, FiCheck, FiArrowRight } = FiIcons;

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' }
];

function classifyEvent(event, sessions, now) {
  const eventSessions = sessions.filter((s) => s.event_id === event.id);
  if (!eventSessions.length) return 'upcoming';
  const startTimes = eventSessions.map((s) => new Date(s.start_time).getTime()).filter(Boolean);
  const endTimes = eventSessions.map((s) => new Date(s.end_time || s.start_time).getTime()).filter(Boolean);
  const hasFuture = startTimes.some((t) => t > now);
  const allPast = endTimes.length && endTimes.every((t) => t < now);
  if (allPast) return 'completed';
  const anyStarted = startTimes.some((t) => t <= now);
  if (anyStarted && hasFuture) return 'in_progress';
  return 'upcoming';
}

function getNextSession(sessions, eventId) {
  const now = Date.now();
  const eventSessions = sessions
    .filter((s) => s.event_id === eventId && new Date(s.start_time).getTime() >= now)
    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  return eventSessions[0] ?? null;
}

function isSessionJoinable(session, event) {
  const now = Date.now();
  const start = session?.start_time ? new Date(session.start_time).getTime() : 0;
  const end = session?.end_time ? new Date(session.end_time).getTime() : start + 3600000;
  const fifteenMin = 15 * 60 * 1000;
  return start && (now >= start - fifteenMin && now <= end);
}

function getJoinUrl(session, event) {
  return (
    session?.zoom_meeting_link ||
    session?.meeting_url ||
    event?.zoom_meeting_link
  );
}

export default function MyEvents() {
  const { profile } = useAuth();
  const { events: registeredEvents = [], loading: eventsLoading } = useDashboardEvents();
  const [tab, setTab] = useState('all');
  const [sessionsByEvent, setSessionsByEvent] = useState({});
  const [progressByEvent, setProgressByEvent] = useState({});
  const [loading, setLoading] = useState(true);

  const studentId = profile?.id ?? null;

  useEffect(() => {
    if (!studentId || !registeredEvents.length) {
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      const eventIds = registeredEvents.map((e) => e.id).filter(Boolean);
      const allSessions = [];
      const progressMap = {};

      await Promise.all(
        eventIds.map(async (eventId) => {
          const [sessRes, progRes] = await Promise.all([
            eventsService.getEventSessions(eventId),
            progressService.calculateEventProgress(studentId, eventId, { role: 'student', userId: studentId })
          ]);
          if (sessRes.data) allSessions.push(...sessRes.data.map((s) => ({ ...s, event_id: eventId })));
          if (progRes.data) progressMap[eventId] = progRes.data;
        })
      );

      setSessionsByEvent(
        allSessions.reduce((acc, s) => {
          if (!acc[s.event_id]) acc[s.event_id] = [];
          acc[s.event_id].push(s);
          return acc;
        }, {})
      );
      setProgressByEvent(progressMap);
      setLoading(false);
    })();
  }, [studentId, registeredEvents.length, registeredEvents.map((e) => e.id).join(',')]);

  const now = Date.now();
  const allSessions = Object.values(sessionsByEvent).flat();
  const eventsWithClassification = registeredEvents.map((event) => ({
    ...event,
    _classification: classifyEvent(event, allSessions, now),
    _nextSession: getNextSession(allSessions, event.id),
    _sessions: sessionsByEvent[event.id] ?? [],
    _progress: progressByEvent[event.id]
  }));

  const filtered =
    tab === 'all'
      ? eventsWithClassification
      : eventsWithClassification.filter((e) => e._classification === tab);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">My Events</h1>
        <p className="text-slate-600 mt-1">View and join your registered events</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-[#00AAFF] text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {eventsLoading || loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-slate-200 h-48 bg-slate-50 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <SafeIcon icon={FiCalendar} className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600">No events in this tab</p>
          <p className="text-sm text-slate-500 mt-1">
            {tab === 'all' ? 'Register for events from the dashboard.' : `No ${tab.replace('_', ' ')} events.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((event) => {
            const nextSession = event._nextSession;
            const joinUrl = nextSession && getJoinUrl(nextSession, event);
            const canJoin = nextSession && isSessionJoinable(nextSession, event);
            const totalSessions = event._sessions?.length ?? 0;
            const pct = event._progress?.percentage ?? 0;
            const locationText = formatEventLocationDisplay(event);

            return (
              <div
                key={event.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                {(event.featured_image_url || event.image_url) && (
                  <div className="aspect-video bg-slate-100">
                    <img
                      src={event.featured_image_url || event.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        event._classification === 'completed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : event._classification === 'in_progress'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {event._classification.replace('_', ' ')}
                    </span>
                  </div>
                  <Link to={`/dashboard/student/events/${event.id}`} className="font-semibold text-slate-900 line-clamp-2 hover:text-[#00AAFF] transition-colors block">
                    {event.title}
                  </Link>
                  {locationText && (
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{locationText}</p>
                  )}
                  {totalSessions > 0 && (
                    <p className="text-sm text-slate-500 mt-1">
                      Progress: {pct}% · {totalSessions} session{totalSessions !== 1 ? 's' : ''}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {canJoin && joinUrl && (
                      <a
                        href={joinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#00AAFF] text-white text-sm font-medium hover:opacity-90"
                      >
                        <SafeIcon icon={FiPlay} className="w-4 h-4" />
                        Join next session
                      </a>
                    )}
                    <Link
                      to={`/dashboard/student/events/${event.id}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
                    >
                      View details <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
